//////////////////////////////////////////////////
// Home Screen
//////////////////////////////////////////////////

import {libInit, getUrlVars, gn, isAndroid, newHTML} from '../utils/lib';
import ScratchAudio from '../utils/ScratchAudio';
import OS from '../tablet/OS';
import IO from '../tablet/IO';
import Localization from '../utils/Localization';
import Cookie from '../utils/Cookie';
import Events from '../utils/Events';

import Home from './Home';
let lobbyFirstLoad = true;
import Samples from './Samples';
import ActivityHub from './ActivityHub';

let version = undefined;
let busy = false;
let errorTimer;
const host = 'inapp/';
let currentPage = null;
let currentActivityHub = null;
let appInitPending = false;
const DEFAULT_PROFILE_ID = 'default';
const PROFILE_HOLD_MOUSE_SUPPRESSION_MS = 750;

export default class Lobby {
    // Getters/setters for properties used in other classes
    static get version () {
        return version;
    }

    static set busy (newBusy) {
        busy = newBusy;
    }

    static get errorTimer () {
        return errorTimer;
    }

    static appinit (v) {
        var urlvars = getUrlVars();
        var place = urlvars.place;
        const requestedPlace = place ? place : 'home';
        const initialPlace = requestedPlace === 'activity' && !Lobby.isActivityEnabled() ? 'home' : requestedPlace;
        const wrap = gn('wrapc');

        if (appInitPending) {
            console.log('⏭️ Lobby.appinit() skipped - initialization already in progress');
            return;
        }

        if (version === v && currentPage === initialPlace && wrap && wrap.childElementCount > 0) {
            console.log('⏭️ Lobby.appinit() skipped - page already initialized for', initialPlace);
            return;
        }

        appInitPending = true;
        console.log('🚀 Lobby.appinit() called with version:', v);
        try {
            libInit();
            version = v;
            console.log('📍 URL vars - place:', place, 'all vars:', urlvars);
            ScratchAudio.addSound('sounds/', 'tap.wav', ScratchAudio.uiSounds);
            ScratchAudio.addSound('sounds/', 'cut.wav', ScratchAudio.uiSounds);
            ScratchAudio.init();
            console.log('📍 About to call Lobby.setPage with:', initialPlace);
            Lobby.setPage(initialPlace);

            if (window.Settings.settingsPageDisabled) {
                gn('settings').style.visibility = 'hidden';
            }

            gn('hometab').onclick = function () {
                if (gn('hometab').className != 'home on') {
                    Lobby.setPage('home');
                }
            };
            gn('helptab').onclick = function () {
                if (gn('helptab').className != 'help on') {
                    Lobby.setPage('help');
                }
            };
            if (gn('activitytab')) {
                gn('activitytab').onclick = function () {
                    if (gn('activitytab').className != 'activity on') {
                        Lobby.setPage('activity');
                    }
                };
            }
            gn('booktab').onclick = function () {
                if (gn('booktab').className != 'book on') {
                    Lobby.setPage('book');
                }
            };
            gn('geartab').onclick = function () {
                if (gn('geartab').className != 'gear on') {
                    Lobby.setPage('gear');
                }
            };
            gn('abouttab').onclick = function () {
                if (gn('abouttab').className != 'tab on') {
                    Lobby.setSubMenu('about');
                }
            };
            gn('interfacetab').onclick = function () {
                if (gn('interfacetab').className != 'tab on') {
                    Lobby.setSubMenu('interface');
                }
            };
            gn('painttab').onclick = function () {
                if (gn('painttab').className != 'tab on') {
                    Lobby.setSubMenu('paint');
                }
            };
            gn('blockstab').onclick = function () {
                if (gn('blockstab').className != 'tab on') {
                    Lobby.setSubMenu('blocks');
                }
            };
            // Privacy tab
            if (gn('privacytab')) {
                gn('privacytab').onclick = function () {
                    if (gn('privacytab').className != 'tab2 on') {
                        Lobby.setSubMenu('privacy');
                    }
                };
            }
            if (isAndroid) {
                AndroidInterface.notifyDoneLoading();
            }
        } finally {
            appInitPending = false;
        }
    }

    static isActivityEnabled () {
        return !(window.__FF && window.__FF.activityEngine === false);
    }

    static isUserPaidTier () {
        try {
            const tier = String(
                localStorage.getItem('scratchjr_subscription_tier') ||
                sessionStorage.getItem('scratchjr_subscription_tier') ||
                ''
            ).trim().toLowerCase();

            return Lobby.isPaidTier(tier) || Lobby.hasActivePremiumWindow(
                localStorage.getItem('scratchjr_premium_expires_at') ||
                sessionStorage.getItem('scratchjr_premium_expires_at') ||
                ''
            );
        } catch (_) {
            return false;
        }
    }

    static isPremiumUpgradeEnabled () {
        try {
            if (window.controlTower && typeof window.controlTower.getFlag === 'function') {
                return window.controlTower.getFlag('premiumUpgrade', true) !== false;
            }
        } catch (_) {}

        try {
            return !(window.__FF && window.__FF.premiumUpgrade === false);
        } catch (_) {
            return true;
        }
    }

    static claimPanelRenderVersion (container, key) {
        const prop = `__${key}RenderVersion`;
        const nextVersion = (Number(container[prop]) || 0) + 1;
        container[prop] = nextVersion;
        return nextVersion;
    }

    static isPanelRenderCurrent (container, key, version) {
        const prop = `__${key}RenderVersion`;
        return !!container && container.isConnected && container[prop] === version;
    }

    static isPaidTier (tier) {
        const normalizedTier = String(tier || '').trim().toLowerCase();
        return normalizedTier === 'premium' || normalizedTier === 'family' || normalizedTier === 'school';
    }

    static hasActivePremiumWindow (premiumExpiresAt) {
        if (!premiumExpiresAt) {
            return false;
        }

        const expiryDate = new Date(premiumExpiresAt);
        return !Number.isNaN(expiryDate.getTime()) && expiryDate.getTime() > Date.now();
    }

    static setPage (page) {
        console.log('📍 Lobby.setPage() called with page:', page, 'busy:', busy);
        if (busy) {
            console.log('❌ Lobby.setPage() BLOCKED - busy flag is true!');
            return;
        }
        if (gn('hometab').className == 'home on') {
            console.log('✅ Home tab already on, saving scroll position');
            var doNext = function (page) {
                Lobby.changePage(page);
            };
            OS.setfile('homescroll.sjr', gn('wrapc').scrollTop, function () {
                doNext(page);
            });
        } else {
            console.log('✅ Home tab not on, changing page directly');
            Lobby.changePage(page);
        }
    }

    static changePage (page) {
        console.log('🔄 Lobby.changePage() called with page:', page);
        Lobby.selectButton(page);
        document.documentElement.scrollTop = 0;
        var div = gn('wrapc');
        if (currentActivityHub) {
            currentActivityHub.destroy();
            currentActivityHub = null;
        }
        while (div.childElementCount > 0) {
            div.removeChild(div.childNodes[0]);
        }
        switch (page) {
        case 'home':
            console.log('🏠 Loading home projects page, setting busy = true');
            busy = true;
            // Suppress the initial auto-navigation click sound on first load
            try {
                if (lobbyFirstLoad) {
                    lobbyFirstLoad = false;
                } else {
                    ScratchAudio.sndFX('tap.wav');
                }
            } catch (_) {}
            Lobby.loadProjects(div);
            break;
        case 'help':
            busy = true;
            ScratchAudio.sndFX('tap.wav');
            Lobby.loadSamples(div);
            break;
        case 'activity':
            if (!Lobby.isActivityEnabled()) {
                Lobby.changePage('home');
                return;
            }
            busy = true;
            ScratchAudio.sndFX('tap.wav');
            Lobby.loadActivities(div);
            break;
        case 'book':
            Lobby.loadGuide(div);
            break;
        case 'gear':
            ScratchAudio.sndFX('tap.wav');
            Lobby.loadSettings(div);
            break;
        default:
            break;
        }
        currentPage = page;
    }

    static loadProjects (p) {
        console.log('📂 Lobby.loadProjects() called');
        document.ontouchmove = undefined;
        document.onmousemove = undefined;
        gn('topsection').className = 'topsection home';
        // Set localized header with safe fallback and update when localization becomes ready
        function setHeaderLocalized() {
            try {
                const t = Localization.localize('MY_PROJECTS');
                if (t && t.indexOf('String missing:') !== 0) {
                    gn('tabheader').textContent = t;
                    return;
                }
            } catch (_) {}
            gn('tabheader').textContent = 'My Projects';
        }
        // Apply immediately if localization ready already, otherwise wait for event
        if (typeof window !== 'undefined' && window.__LOCALIZATION_READY__) { setHeaderLocalized(); }
        try { window.addEventListener('localization:ready', setHeaderLocalized, { once: true }); } catch (_) {}
        // Subtitle (localized with fallback)
        function setSubtitleLocalized() {
            try {
                const s = Localization.localize('SAMPLE_PROJECTS');
                if (s && s.indexOf('String missing:') !== 0) { gn('subtitle').textContent = s; return; }
            } catch (_) {}
            gn('subtitle').textContent = '';
        }
        if (typeof window !== 'undefined' && window.__LOCALIZATION_READY__) { setSubtitleLocalized(); }
        try { window.addEventListener('localization:ready', setSubtitleLocalized, { once: true }); } catch (_) {}
        gn('footer').className = 'footer off';
        // Hide licensing text when footer tabs are hidden
        const inappLicensing = document.getElementById('inapp-licensing');
        if (inappLicensing) {
            inappLicensing.style.transform = 'translateY(10.4vh)';
            setTimeout(() => {
                inappLicensing.style.display = 'none';
            }, 300);
        }
        gn('wrapc').scrollTop = 0;
        gn('wrapc').className = 'contentwrap scroll';
        var div = newHTML('div', 'htmlcontents home', p);
        div.setAttribute('id', 'htmlcontents');
        console.log('✅ About to call Home.init()');
        Home.init();
    }

    static loadSamples (p) {
        gn('topsection').className = 'topsection help';
        gn('tabheader').textContent = Localization.localize('QUICK_INTRO');
        gn('subtitle').textContent = Localization.localize('SAMPLE_PROJECTS');
        gn('footer').className = 'footer off';
        // Hide licensing text when footer tabs are hidden
        const inappLicensing = document.getElementById('inapp-licensing');
        if (inappLicensing) {
            inappLicensing.style.transform = 'translateY(10.4vh)';
            setTimeout(() => {
                inappLicensing.style.display = 'none';
            }, 300);
        }
        gn('wrapc').scrollTop = 0;
        gn('wrapc').className = 'contentwrap noscroll';
        var div = newHTML('div', 'htmlcontents help', p);
        div.setAttribute('id', 'htmlcontents');
        document.ontouchmove = function (e) {
            e.preventDefault();
        };
        document.onmousemove = function (e) {
            e.preventDefault();
        };
        Samples.init();
    }

    static loadActivities (p) {
        document.ontouchmove = undefined;
        document.onmousemove = undefined;
        gn('topsection').className = 'topsection activity compact';

        const title = Lobby.localizeOrFallback('ACTIVITIES', 'Activities');
        gn('tabheader').textContent = title;
        gn('subtitle').textContent = '';
        gn('footer').className = 'footer off';

        const inappLicensing = document.getElementById('inapp-licensing');
        if (inappLicensing) {
            inappLicensing.style.transform = 'translateY(10.4vh)';
            setTimeout(() => {
                inappLicensing.style.display = 'none';
            }, 300);
        }

        gn('wrapc').scrollTop = 0;
        gn('wrapc').className = 'contentwrap scroll';

        var div = newHTML('div', 'htmlcontents activity compact-header', p);
        div.setAttribute('id', 'htmlcontents');

        currentActivityHub = new ActivityHub(div);
        currentActivityHub.init()
            .catch((err) => {
                console.error('[Lobby] Failed to initialize ActivityHub:', err);
            })
            .finally(() => {
                busy = false;
            });
    }

    static loadGuide (p) {
        gn('topsection').className = 'topsection book';
        gn('footer').className = 'footer on';
        // Show licensing text below inapp tabs
        const inappLicensing = document.getElementById('inapp-licensing');
        if (inappLicensing) {
            inappLicensing.style.display = 'block';
            // Slide up from below the footer tabs
            setTimeout(() => {
                inappLicensing.style.transform = 'translateY(0)';
            }, 100);
        }
        var div = newHTML('div', 'htmlcontents home', p);
        div.setAttribute('id', 'htmlcontents');
        setTimeout(function () {
            Lobby.setSubMenu('about');
        }, 250);
    }

    static loadSettings (p) {
        // loadProjects without the header
        gn('topsection').className = 'topsection book';
        gn('footer').className = 'footer off';
        // Hide licensing text when footer tabs are hidden
        const inappLicensing = document.getElementById('inapp-licensing');
        if (inappLicensing) {
            inappLicensing.style.transform = 'translateY(10.4vh)';
            setTimeout(() => {
                inappLicensing.style.display = 'none';
            }, 300);
        }
        gn('wrapc').scrollTop = 0;
        gn('wrapc').className = 'contentwrap scroll';
        var div = newHTML('div', 'htmlcontents settings', p);
        div.setAttribute('id', 'htmlcontents');
        const shell = newHTML('div', 'settings-shell', div);

        const upgradePanel = newHTML('section', 'settings-panel settings-upgrade-settings-panel', shell);
        upgradePanel.setAttribute('id', 'settings-upgrade-panel');
        const initialUpgradeEnabled = Lobby.isPremiumUpgradeEnabled();
        Lobby.renderSettingsUpgradePanel(upgradePanel, {
            upgradeEnabled: initialUpgradeEnabled
        }).catch((error) => {
            console.error('[Lobby] Failed to render upgrade manager:', error);
            upgradePanel.innerHTML = '';
            const title = newHTML('h1', 'localizationtitle', upgradePanel);
            title.textContent = 'Upgrade to Premium';
            const feedback = newHTML('div', 'settings-feedback error', upgradePanel);
            feedback.textContent = 'We could not load premium upgrade options right now.';
        });

        if (!(window.controlTower && window.controlTower.initialized)) {
            const rerenderUpgradePanel = function () {
                if (currentPage !== 'gear' || !upgradePanel.isConnected) {
                    return;
                }

                const nextUpgradeEnabled = Lobby.isPremiumUpgradeEnabled();
                if (nextUpgradeEnabled === initialUpgradeEnabled) {
                    return;
                }

                Lobby.renderSettingsUpgradePanel(upgradePanel, {
                    upgradeEnabled: nextUpgradeEnabled
                }).catch((error) => {
                    console.error('[Lobby] Failed to re-render upgrade manager after flags loaded:', error);
                });
            };

            try {
                window.addEventListener('controltower:ready', rerenderUpgradePanel, { once: true });
            } catch (_) {}
        }

        const accountPanel = newHTML('section', 'settings-panel settings-account-panel', shell);
        accountPanel.setAttribute('id', 'settings-account-panel');
        Lobby.renderAccountManager(accountPanel).catch((error) => {
            console.error('[Lobby] Failed to render account manager:', error);
            accountPanel.innerHTML = '';
            const title = newHTML('h2', 'settings-panel-title', accountPanel);
            title.textContent = 'Child Profiles';
            const feedback = newHTML('div', 'settings-feedback error', accountPanel);
            feedback.textContent = 'We could not load child profiles right now.';
        });

        const languagePanel = newHTML('section', 'settings-panel settings-language-panel', shell);

        var title = newHTML('h1', 'localizationtitle', languagePanel);
        const updateLanguageTitle = () => {
            title.textContent = Lobby.localizeWithFallback('SELECT_LANGUAGE', 'Select Language');
        };
        updateLanguageTitle();
        if (!window.__LOCALIZATION_READY__) {
            window.addEventListener('localization:ready', updateLanguageTitle, {once: true});
        }

        var languageButtons = newHTML('div', 'languagebuttons', languagePanel);

        var languageButton;
        for (var l in window.Settings.supportedLocales) {
            var selected = '';
            if (window.Settings.supportedLocales[l] == Localization.currentLocale) {
                selected = ' selected';
            }
            languageButton = newHTML('div', 'localizationselect' + selected, languageButtons);
            languageButton.textContent = l;

            languageButton.onclick = function (e) {
                ScratchAudio.sndFX('tap.wav');
                let newLocale = window.Settings.supportedLocales[e.target.textContent];
                Cookie.set('localization', newLocale);
                Lobby.rememberSettingsScrollTarget('language');
                OS.analyticsEvent('lobby', 'language_changed', newLocale);
                window.location = '?place=gear&settingsScrollTarget=language';
            };
        }

        const pendingScrollTarget = Lobby.getSettingsScrollTargetFromUrl() || Lobby.consumeSettingsScrollTarget();
        if (pendingScrollTarget === 'language') {
            [100, 250, 500].forEach((delay) => {
                setTimeout(() => {
                    Lobby.scrollSettingsPanelIntoView(languagePanel);
                }, delay);
            });
            Lobby.clearSettingsScrollTargetFromUrl();
        }
    }

    static rememberSettingsScrollTarget (target) {
        try {
            sessionStorage.setItem('scratchjr_settings_scroll_target', target);
        } catch (_) {}
    }

    static consumeSettingsScrollTarget () {
        try {
            const target = sessionStorage.getItem('scratchjr_settings_scroll_target') || '';
            sessionStorage.removeItem('scratchjr_settings_scroll_target');
            return target;
        } catch (_) {
            return '';
        }
    }

    static scrollSettingsPanelIntoView (panel) {
        if (!panel) {
            return;
        }

        try {
            panel.scrollIntoView({
                behavior: 'auto',
                block: 'start'
            });
        } catch (_) {}

        try {
            const wrap = gn('wrapc');
            if (wrap) {
                const wrapRect = wrap.getBoundingClientRect();
                const panelRect = panel.getBoundingClientRect();
                const desiredTop = wrap.scrollTop + (panelRect.top - wrapRect.top) - 12;
                wrap.scrollTop = Math.max(0, desiredTop);
            }
        } catch (_) {}
    }

    static getSettingsScrollTargetFromUrl () {
        try {
            const url = new URL(window.location.href);
            return url.searchParams.get('settingsScrollTarget') || '';
        } catch (_) {
            return '';
        }
    }

    static clearSettingsScrollTargetFromUrl () {
        try {
            const url = new URL(window.location.href);
            if (!url.searchParams.has('settingsScrollTarget')) {
                return;
            }
            url.searchParams.delete('settingsScrollTarget');
            const nextSearch = url.searchParams.toString();
            const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash || ''}`;
            window.history.replaceState({}, '', nextUrl);
        } catch (_) {}
    }

    static localizeWithFallback (key, fallbackText, formatting) {
        const localizedText = Localization.localizeOptional(key, formatting);
        if (localizedText && localizedText !== key && localizedText !== `String missing: ${key}`) {
            return localizedText;
        }
        return fallbackText;
    }

    static async renderSettingsUpgradePanel (container, options = {}) {
        const renderVersion = Lobby.claimPanelRenderVersion(container, 'settingsUpgrade');
        const upgradeEnabled = Object.prototype.hasOwnProperty.call(options, 'upgradeEnabled')
            ? options.upgradeEnabled !== false
            : Lobby.isPremiumUpgradeEnabled();

        if (!upgradeEnabled) {
            container.innerHTML = '';
            container.hidden = true;
            container.style.display = 'none';
            container.setAttribute('aria-hidden', 'true');
            return;
        }

        container.hidden = false;
        container.style.display = '';
        container.removeAttribute('aria-hidden');

        return Lobby.renderUpgradeManager(container, Object.assign({}, options, {
            _renderVersion: renderVersion
        }));
    }

    static async renderUpgradeManager (container, options = {}) {
        const renderVersion = options._renderVersion || Lobby.claimPanelRenderVersion(container, 'settingsUpgrade');
        const isStale = function () {
            return !Lobby.isPanelRenderCurrent(container, 'settingsUpgrade', renderVersion);
        };

        const session = Lobby.getAuthSession();
        const isAuthenticated = !!(session.userId && session.email);
        let upgradeState = Lobby.getStoredBillingState(session);
        let feedbackMessage = options.feedback || '';
        let feedbackTone = options.feedbackTone || '';

        const returnFeedback = await Lobby.consumeUpgradeReturn();
        if (isStale()) {
            return;
        }
        if (returnFeedback) {
            feedbackMessage = returnFeedback.feedback || feedbackMessage;
            feedbackTone = returnFeedback.feedbackTone || feedbackTone;
            if (returnFeedback.status) {
                upgradeState = Lobby.mergeBillingState(upgradeState, returnFeedback.status);
                Lobby.persistBillingState(upgradeState);
            }
        }

        if (isStale()) {
            return;
        }

        Lobby.renderUpgradeManagerContent(container, {
            feedback: feedbackMessage,
            feedbackTone: feedbackTone,
            session: session,
            upgradeState: upgradeState
        });

        if (isAuthenticated) {
            try {
                const billingStatus = await Lobby.fetchBillingStatus();
                if (isStale()) {
                    return;
                }
                const nextUpgradeState = Lobby.mergeBillingState(upgradeState, billingStatus);
                Lobby.persistBillingState(nextUpgradeState);

                if (Lobby.hasBillingStateChanged(upgradeState, nextUpgradeState)) {
                    Lobby.renderUpgradeManagerContent(container, {
                        feedback: feedbackMessage,
                        feedbackTone: feedbackTone,
                        session: session,
                        upgradeState: nextUpgradeState
                    });
                }
            } catch (error) {
                if (isStale()) {
                    return;
                }
                if (await Lobby.recoverFromMissingBillingAccount(container, error)) {
                    return;
                }
                console.warn('[Lobby] Billing status unavailable:', error && error.message ? error.message : error);
            }
        }

    }

    static renderUpgradeManagerContent (container, options = {}) {
        const session = options.session || Lobby.getAuthSession();
        const isAuthenticated = !!(session.userId && session.email);
        const upgradeState = options.upgradeState || Lobby.getStoredBillingState(session);
        const feedbackMessage = options.feedback || '';
        const feedbackTone = options.feedbackTone || '';
        const normalizedUpgradeTier = String(upgradeState.tier || '').trim().toLowerCase();

        container.innerHTML = '';

        const title = newHTML('h1', 'localizationtitle settings-upgrade-heading', container);
        title.textContent = 'Upgrade to Premium';

        const feedback = newHTML('div', `settings-feedback${feedbackTone ? ` ${feedbackTone}` : ''}`, container);
        feedback.setAttribute('id', 'settings-upgrade-feedback');
        feedback.textContent = feedbackMessage;

        if (feedbackMessage && feedbackTone === 'success') {
            setTimeout(function () {
                feedback.style.transition = 'opacity 0.5s ease';
                feedback.style.opacity = '0';
                setTimeout(function () { feedback.textContent = ''; feedback.style.opacity = ''; }, 500);
            }, 8000);
        }

        const copy = newHTML('div', 'settings-upgrade-copy description', container);
        copy.setAttribute('id', 'settings-upgrade-copy');
        copy.textContent = 'Donate $4.99 to support Cultivate Learning at the University of Washington. Your donation unlocks Bluux Premium for 30 days and can be renewed any time.';

        const partnerTitle = newHTML('p', 'settings-section-subtitle settings-upgrade-section-title', container);
        partnerTitle.textContent = 'Research partner';

        const partnerLink = newHTML('a', 'settings-upgrade-partner', container);
        partnerLink.setAttribute('id', 'settings-upgrade-partner-link');
        partnerLink.href = 'https://cultivatelearning.uw.edu/';
        partnerLink.target = '_blank';
        partnerLink.rel = 'noopener noreferrer';
        partnerLink.setAttribute('aria-label', 'Visit Cultivate Learning at the University of Washington');

        const partnerLogo = document.createElement('img');
        partnerLogo.className = 'settings-upgrade-partner-logo';
        partnerLogo.src = 'assets/ui/cultivate-learning-logo.png';
        partnerLogo.alt = 'Cultivate Learning at the University of Washington';
        partnerLink.appendChild(partnerLogo);

        const partnerCopy = newHTML('div', 'settings-upgrade-partner-copy', partnerLink);
        const partnerName = newHTML('span', 'settings-upgrade-partner-name', partnerCopy);
        partnerName.textContent = 'Cultivate Learning at the University of Washington';
        const partnerUrl = newHTML('span', 'settings-upgrade-partner-url', partnerCopy);
        partnerUrl.textContent = 'cultivatelearning.uw.edu';

        const status = newHTML('p', 'settings-upgrade-status', container);
        status.setAttribute('id', 'settings-upgrade-status');

        if (!isAuthenticated) {
            status.textContent = 'Sign in on the home page to upgrade this adult account.';

            const actions = newHTML('div', 'languagebuttons settings-upgrade-actions', container);
            const signInButton = document.createElement('button');
            signInButton.type = 'button';
            signInButton.setAttribute('id', 'settings-upgrade-signin-btn');
            signInButton.className = 'localizationselect settings-action-button settings-action-button-secondary';
            signInButton.textContent = 'Go To Sign In';
            signInButton.onclick = function () {
                ScratchAudio.sndFX('tap.wav');
                window.location.href = 'index.html';
            };
            actions.appendChild(signInButton);
            Lobby.appendUpgradeSupportLink(container);
            return;
        }

        const daysRemaining = Lobby.getPremiumDaysRemaining(upgradeState.premiumExpiresAt);
        const isActivePremium = normalizedUpgradeTier === 'premium' && daysRemaining > 0;
        const isExpiredPremium = normalizedUpgradeTier === 'premium' && !!upgradeState.premiumExpiresAt && daysRemaining === 0;

        if (normalizedUpgradeTier) {
            Lobby.persistBillingState(upgradeState);
        }

        if (isActivePremium) {
            status.className = 'settings-upgrade-status active';
            status.textContent = `Premium active through ${Lobby.formatPremiumExpiry(upgradeState.premiumExpiresAt)} · ${daysRemaining} days remaining.`;
        } else if (isExpiredPremium) {
            status.className = 'settings-upgrade-status expired';
            status.textContent = 'Premium access has ended. Donate again to reactivate this account.';
        } else {
            status.className = 'settings-upgrade-status';
            status.textContent = 'Stripe Checkout opens in a secure page and returns to Bluux to activate premium on this adult account.';
        }

        const actions = newHTML('div', 'languagebuttons settings-upgrade-actions', container);
        const upgradeButton = document.createElement('button');
        upgradeButton.type = 'button';
        upgradeButton.setAttribute('id', 'settings-upgrade-btn');
        upgradeButton.className = 'localizationselect settings-action-button settings-action-button-primary settings-upgrade-cta';
        upgradeButton.textContent = isActivePremium ? 'Extend Premium' : 'Donate';
        upgradeButton.onclick = async function () {
            ScratchAudio.sndFX('tap.wav');
            const originalText = upgradeButton.textContent;
            upgradeButton.disabled = true;
            upgradeButton.textContent = 'Opening Stripe\u2026';
            upgradeButton.classList.add('settings-upgrade-cta-loading');
            try {
                const checkoutUrl = await Lobby.createPremiumCheckout();
                window.location.href = checkoutUrl;
            } catch (error) {
                console.error('Checkout error:', error);
                if (await Lobby.recoverFromMissingBillingAccount(container, error)) {
                    return;
                }
                await Lobby.renderSettingsUpgradePanel(container, {
                    feedback: error && error.message ? error.message : 'Unable to start Stripe Checkout right now.',
                    feedbackTone: 'error'
                });
            } finally {
                upgradeButton.disabled = false;
                upgradeButton.textContent = originalText;
                upgradeButton.classList.remove('settings-upgrade-cta-loading');
            }
        };
        actions.appendChild(upgradeButton);

        if (isActivePremium) {
            const note = newHTML('div', 'settings-upgrade-note description', container);
            note.textContent = 'Donate again to extend premium by another 30 days.';
        }

        Lobby.appendUpgradeSupportLink(container);
    }

    static appendUpgradeSupportLink (container) {
        const supportNote = newHTML('p', 'settings-upgrade-note settings-upgrade-support description', container);
        supportNote.appendChild(document.createTextNode('Need help with premium? Contact '));

        const supportLink = newHTML('a', 'settings-upgrade-support-link', supportNote);
        supportLink.href = 'mailto:support@bluux.ai';
        supportLink.textContent = 'support@bluux.ai';
        supportLink.setAttribute('aria-label', 'Email Bluux support');
    }

    static async renderAccountManager (container, options = {}) {
        container.innerHTML = '';

        const title = newHTML('h1', 'localizationtitle settings-section-title', container);
        title.textContent = 'Child Profiles';

        const status = newHTML('p', 'settings-account-status', container);
        status.setAttribute('id', 'settings-account-status');

        const feedback = newHTML('div', `settings-feedback${options.feedbackTone ? ` ${options.feedbackTone}` : ''}`, container);
        feedback.setAttribute('id', 'settings-profile-feedback');
        feedback.textContent = options.feedback || '';

        const session = Lobby.getAuthSession();
        const isAuthenticated = !!(session.userId && session.email);

        if (!isAuthenticated) {
            status.textContent = 'Sign in on the home page to create child spaces.';
            const signInButton = newHTML('button', 'localizationselect settings-action-button settings-action-button-secondary', container);
            signInButton.type = 'button';
            signInButton.setAttribute('id', 'settings-sign-in-btn');
            signInButton.textContent = 'Go To Sign In';
            signInButton.onclick = function () {
                ScratchAudio.sndFX('tap.wav');
                window.location.href = 'index.html';
            };
            return;
        }

        const activeProfileId = Lobby.getActiveProfileId();
        status.innerHTML = '';
        const statusLabel = newHTML('span', 'settings-account-status-label', status);
        statusLabel.textContent = 'Adult account';
        const statusEmail = newHTML('span', 'settings-account-status-email', status);
        statusEmail.textContent = session.email;

        const helper = newHTML('div', 'settings-account-copy description', container);
        helper.textContent = 'Choose a shared space or create a child profile for a dedicated learner.';

        const profileGrid = newHTML('div', 'settings-profile-grid', container);
        profileGrid.setAttribute('id', 'settings-profile-list');

        const profiles = await Lobby.getProfilesForAccount(session.userId);
        const profileCards = [
            {
                id: DEFAULT_PROFILE_ID,
                displayName: 'Shared',
                roleLabel: 'Shared Space',
                implicit: true
            },
            ...profiles.map((profile) => ({
                ...profile,
                roleLabel: 'Child'
            }))
        ];

        for (let i = 0; i < profileCards.length; i++) {
            const profile = profileCards[i];
            const safeId = Lobby.toSafeDomId(profile.id);
            const card = newHTML('button', 'settings-profile-card localizationselect', profileGrid);
            card.type = 'button';
            card.setAttribute('id', `settings-profile-${safeId}`);
            card.setAttribute('data-profile-id', profile.id);
            if (activeProfileId === profile.id) {
                card.className += ' active';
                card.setAttribute('data-active', 'true');
            }

            const avatar = newHTML('div', 'settings-profile-avatar', card);
            avatar.textContent = Lobby.getProfileInitial(profile.displayName);

            const name = newHTML('div', 'settings-profile-name', card);
            name.textContent = profile.displayName;

            const meta = newHTML('div', 'settings-profile-meta', card);
            meta.textContent = profile.roleLabel;

            const badge = newHTML('div', 'settings-profile-badge', card);
            badge.textContent = activeProfileId === profile.id ? 'Using Now' : 'Switch To';

            if (!profile.implicit) {
                const close = newHTML('div', 'closex', card);
                close.setAttribute('aria-hidden', 'true');
                close.style.visibility = 'hidden';
                close.style.pointerEvents = 'none';
            }

            const selectProfile = async function () {
                ScratchAudio.sndFX('tap.wav');
                const selectedName = profile.displayName;
                Lobby.hideProfileDeleteIcons(container);
                await Lobby.activateProfile(profile.id);
                await Lobby.renderAccountManager(container, {
                    feedback: `${selectedName} is active.`,
                    feedbackTone: 'success'
                });
            };

            const deleteProfile = profile.implicit ? null : async function () {
                Lobby.hideProfileDeleteIcons(container);
                card.disabled = true;
                ScratchAudio.sndFX('cut.wav');

                try {
                    const wasActive = Lobby.getActiveProfileId() === profile.id;
                    await Lobby.deleteChildProfile(session.userId, profile.id);
                    if (wasActive) {
                        await Lobby.activateProfile(DEFAULT_PROFILE_ID);
                    }
                    await Lobby.renderAccountManager(container, {
                        feedback: `${profile.displayName} was deleted.`,
                        feedbackTone: 'success'
                    });
                } catch (error) {
                    console.error('[Lobby] Failed to delete child profile:', error);
                    await Lobby.renderAccountManager(container, {
                        feedback: `Could not delete ${profile.displayName} right now.`,
                        feedbackTone: 'error'
                    });
                }
            };

            Lobby.bindProfileCardHandlers(card, container, {
                onSelect: selectProfile,
                onDelete: deleteProfile
            });
        }

        if (profiles.length === 0) {
            const emptyState = newHTML('p', 'settings-account-empty description', container);
            emptyState.textContent = 'Shared is ready now. Add a child profile when you want a separate learner space.';
        }

        const formLabel = newHTML('p', 'settings-account-form-label settings-section-subtitle', container);
        formLabel.textContent = 'Create a child profile';

        const form = newHTML('form', 'settings-child-form', container);
        form.setAttribute('id', 'settings-child-form');

        const input = document.createElement('input');
        input.className = 'settings-input';
        input.setAttribute('id', 'settings-child-profile-name');
        input.setAttribute('type', 'text');
        input.setAttribute('maxlength', '24');
        input.setAttribute('autocomplete', 'off');
        input.setAttribute('placeholder', 'Enter child name');
        form.appendChild(input);

        const submit = document.createElement('button');
        submit.className = 'localizationselect settings-action-button settings-action-button-primary';
        submit.type = 'submit';
        submit.setAttribute('id', 'settings-create-child-profile');
        submit.textContent = 'Add Child';
        form.appendChild(submit);

        form.onsubmit = async function (event) {
            event.preventDefault();
            const displayName = String(input.value || '').trim();
            if (!displayName) {
                await Lobby.renderAccountManager(container, {
                    feedback: 'Add a child name first.',
                    feedbackTone: 'error'
                });
                return;
            }

            const duplicate = profiles.some((profile) => String(profile.displayName || '').trim().toLowerCase() === displayName.toLowerCase());
            if (duplicate) {
                await Lobby.renderAccountManager(container, {
                    feedback: 'That child name is already in use.',
                    feedbackTone: 'error'
                });
                return;
            }

            await Lobby.createChildProfile(session.userId, displayName);
            await Lobby.renderAccountManager(container, {
                feedback: `${displayName} is ready.`,
                feedbackTone: 'success'
            });
        };

        const logoutButton = newHTML('button', 'localizationselect settings-action-button settings-action-button-secondary settings-logout-button', form);
        logoutButton.type = 'button';
        logoutButton.setAttribute('id', 'settings-logout-btn');
        logoutButton.textContent = 'Log Out';
        logoutButton.onclick = async function () {
            ScratchAudio.sndFX('tap.wav');
            await Lobby.logoutToIndex();
        };
    }

    static getAuthSession () {
        try {
            if (window.ScratchJrAuth && typeof window.ScratchJrAuth.getSession === 'function') {
                return window.ScratchJrAuth.getSession() || {};
            }
        } catch (_) {}

        try {
            return {
                userId: localStorage.getItem('scratchjr_user_id'),
                email: localStorage.getItem('scratchjr_email'),
                accessToken: localStorage.getItem('scratchjr_access_token'),
                refreshToken: localStorage.getItem('scratchjr_refresh_token'),
                subscriptionTier: localStorage.getItem('scratchjr_subscription_tier') || 'free',
                premiumExpiresAt: localStorage.getItem('scratchjr_premium_expires_at') || '',
                profileId: localStorage.getItem('scratchjr_profile_id') || sessionStorage.getItem('scratchjr_profile_id') || DEFAULT_PROFILE_ID
            };
        } catch (_) {
            return {};
        }
    }

    static getStoredBillingState (session = {}) {
        return {
            tier: session.subscriptionTier || localStorage.getItem('scratchjr_subscription_tier') || sessionStorage.getItem('scratchjr_subscription_tier') || 'free',
            premiumExpiresAt: session.premiumExpiresAt || localStorage.getItem('scratchjr_premium_expires_at') || sessionStorage.getItem('scratchjr_premium_expires_at') || ''
        };
    }

    static mergeBillingState (currentState, nextState) {
        return {
            tier: nextState && nextState.tier ? nextState.tier : currentState.tier,
            premiumExpiresAt: nextState && nextState.premiumExpiresAt ? nextState.premiumExpiresAt : currentState.premiumExpiresAt
        };
    }

    static hasBillingStateChanged (currentState = {}, nextState = {}) {
        return String(currentState.tier || '').trim() !== String(nextState.tier || '').trim() ||
            String(currentState.premiumExpiresAt || '').trim() !== String(nextState.premiumExpiresAt || '').trim();
    }

    static persistBillingState (billingState = {}) {
        if (!billingState || !billingState.tier) {
            return;
        }

        try {
            const normalizedTier = String(billingState.tier || '').trim().toLowerCase();
            if (!normalizedTier) {
                return;
            }

            localStorage.setItem('scratchjr_subscription_tier', normalizedTier);
            sessionStorage.setItem('scratchjr_subscription_tier', normalizedTier);
            if (billingState.premiumExpiresAt) {
                localStorage.setItem('scratchjr_premium_expires_at', billingState.premiumExpiresAt);
                sessionStorage.setItem('scratchjr_premium_expires_at', billingState.premiumExpiresAt);
            } else {
                localStorage.removeItem('scratchjr_premium_expires_at');
                sessionStorage.removeItem('scratchjr_premium_expires_at');
            }

            if (window.cloudSyncService) {
                if (window.cloudSyncService.authContext) {
                    window.cloudSyncService.authContext.subscriptionTier = normalizedTier;
                }
                if (window.cloudSyncService.authState) {
                    window.cloudSyncService.authState.subscriptionTier = normalizedTier;
                }
                if (typeof window.cloudSyncService._persistSubscriptionTier === 'function') {
                    window.cloudSyncService._persistSubscriptionTier(normalizedTier);
                }
            }

            Lobby.updateActivityNavVisibility(normalizedTier, billingState.premiumExpiresAt);
        } catch (_) {}
    }

    static updateActivityNavVisibility () {
        const activityNav = gn('activities-nav');
        if (!activityNav) {
            return;
        }

        const activityEnabledByFlag = !(window.__FF && window.__FF.activityEngine === false);
        activityNav.style.display = activityEnabledByFlag ? 'inline-block' : 'none';
    }

    static formatPremiumExpiry (premiumExpiresAt) {
        if (!premiumExpiresAt) {
            return 'today';
        }
        try {
            return new Date(premiumExpiresAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (_) {
            return 'today';
        }
    }

    static getPremiumDaysRemaining (premiumExpiresAt) {
        if (!premiumExpiresAt) {
            return 0;
        }
        const expiryDate = new Date(premiumExpiresAt);
        if (Number.isNaN(expiryDate.getTime())) {
            return 0;
        }
        return Math.max(0, Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)));
    }

    static buildSettingsUrl (params = {}) {
        const currentUrl = new URL(window.location.href);
        currentUrl.hash = '';
        currentUrl.search = '';
        currentUrl.searchParams.set('place', 'gear');
        Object.keys(params).forEach((key) => {
            const value = params[key];
            if (value == null || value === '') {
                currentUrl.searchParams.delete(key);
            } else {
                currentUrl.searchParams.set(key, value);
            }
        });
        return currentUrl.toString();
    }

    static getApiBaseUrl () {
        const explicitBase = window.__API_BASE;
        if (explicitBase) {
            return String(explicitBase).replace(/\/$/, '');
        }

        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            const localHost = window.location.hostname === '127.0.0.1' ? '127.0.0.1' : 'localhost';
            return `http://${localHost}:7071/api`;
        }

        return '/api';
    }

    static buildApiUrl (path) {
        const normalizedPath = String(path || '');
        if (!normalizedPath) {
            return Lobby.getApiBaseUrl();
        }

        if (/^https?:\/\//i.test(normalizedPath)) {
            return normalizedPath;
        }

        const apiBase = Lobby.getApiBaseUrl();
        if (normalizedPath.indexOf('/api/') === 0) {
            return `${apiBase}${normalizedPath.slice('/api'.length)}`;
        }

        if (normalizedPath.charAt(0) === '/') {
            return `${apiBase}${normalizedPath}`;
        }

        return `${apiBase}/${normalizedPath}`;
    }

    static getBillingHeaders (extraHeaders = {}) {
        const headers = Object.assign({}, extraHeaders);
        const token = localStorage.getItem('scratchjr_access_token') || '';
        if (token) {
            headers.Authorization = `Bearer ${token}`;
            headers['x-auth-token'] = token;
        }
        return headers;
    }

    static async parseBillingResponse (response) {
        try {
            return await response.json();
        } catch (_) {
            return {};
        }
    }

    static buildBillingError (response, data, fallbackMessage) {
        const error = new Error(data.error || fallbackMessage);
        error.status = response.status;
        error.response = data;
        error.isAccountMissing = String(data.error || '').trim().toLowerCase() === 'account not found';
        return error;
    }

    static isLocalRuntime () {
        const hostname = window.location && window.location.hostname ? window.location.hostname : '';
        return hostname === 'localhost' || hostname === '127.0.0.1';
    }

    static getMissingAccountMessage () {
        if (Lobby.isLocalRuntime()) {
            return 'This local test session is stale. Sign in again or create a new local account.';
        }
        return 'This session is no longer valid. Sign in again.';
    }

    static async recoverFromMissingBillingAccount (container, error) {
        if (!(error && error.isAccountMissing)) {
            return false;
        }

        console.warn('[Lobby] Clearing stale session after missing account response.');
        Lobby.clearLocalSession();

        if (container) {
            await Lobby.renderSettingsUpgradePanel(container, {
                feedback: Lobby.getMissingAccountMessage(),
                feedbackTone: 'error'
            });
        }

        return true;
    }

    static async billingFetch (path, init = {}, allowRefreshRetry = true) {
        const requestUrl = Lobby.buildApiUrl(path);
        const timeoutMs = typeof init.timeoutMs === 'number' && init.timeoutMs > 0 ? init.timeoutMs : 0;

        const performRequest = async () => {
            const requestInit = Object.assign({}, init, {
                headers: Lobby.getBillingHeaders(init.headers || {})
            });
            delete requestInit.timeoutMs;

            let timeoutId = null;
            let abortController = null;
            if (timeoutMs && typeof AbortController === 'function') {
                abortController = new AbortController();
                requestInit.signal = abortController.signal;
                timeoutId = setTimeout(() => abortController.abort(), timeoutMs);
            }

            try {
                return await fetch(requestUrl, requestInit);
            } catch (error) {
                if (error && error.name === 'AbortError') {
                    const timeoutError = new Error('Billing request timed out.');
                    timeoutError.code = 'BILLING_TIMEOUT';
                    throw timeoutError;
                }
                throw error;
            } finally {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            }
        };

        let response = await performRequest();
        if (response.status === 401 && allowRefreshRetry && window.ScratchJrAuth && typeof window.ScratchJrAuth.refreshToken === 'function') {
            const refreshed = await window.ScratchJrAuth.refreshToken();
            if (refreshed) {
                response = await performRequest();
            }
        }

        return response;
    }

    static async fetchBillingStatus () {
        var response = await Lobby.billingFetch('/api/billing/status', {
            method: 'GET',
            timeoutMs: 10000
        });

        // Retry once after 2s for server errors
        if (response.status >= 500) {
            await new Promise(function (resolve) { setTimeout(resolve, 2000); });
            response = await Lobby.billingFetch('/api/billing/status', {
                method: 'GET',
                timeoutMs: 10000
            });
        }

        const data = await Lobby.parseBillingResponse(response);
        if (!response.ok) {
            throw Lobby.buildBillingError(response, data, 'Unable to load billing status.');
        }
        return data;
    }

    static async createPremiumCheckout () {
        const response = await Lobby.billingFetch('/api/billing/create-checkout', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                successUrl: Lobby.buildSettingsUrl({upgradeCheckout: 'success'}),
                cancelUrl: Lobby.buildSettingsUrl({upgradeCheckout: 'cancelled'})
            })
        });
        const data = await Lobby.parseBillingResponse(response);
        if (!response.ok || !data.url) {
            throw Lobby.buildBillingError(response, data, 'Unable to start Stripe Checkout right now.');
        }
        return data.url;
    }

    static async confirmCheckoutSession (sessionId) {
        const response = await Lobby.billingFetch('/api/billing/confirm', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({sessionId: sessionId})
        });
        const data = await Lobby.parseBillingResponse(response);
        if (!response.ok) {
            throw Lobby.buildBillingError(response, data, 'Unable to confirm Stripe Checkout.');
        }
        Lobby.persistBillingState(data);
        return data;
    }

    static async consumeUpgradeReturn () {
        let url;
        try {
            url = new URL(window.location.href);
        } catch (_) {
            return null;
        }

        const upgradeCheckout = url.searchParams.get('upgradeCheckout');
        if (!upgradeCheckout) {
            return null;
        }

        const sessionId = url.searchParams.get('session_id');
        url.searchParams.delete('upgradeCheckout');
        url.searchParams.delete('session_id');
        url.searchParams.set('place', 'gear');
        window.history.replaceState({}, '', `${url.pathname}?${url.searchParams.toString()}`);

        if (upgradeCheckout === 'cancelled') {
            return {
                feedback: 'Stripe Checkout was canceled.',
                feedbackTone: ''
            };
        }

        if (upgradeCheckout !== 'success') {
            return null;
        }

        if (!sessionId) {
            return {
                feedback: 'Stripe Checkout did not return a session ID.',
                feedbackTone: 'error'
            };
        }

        try {
            const status = await Lobby.confirmCheckoutSession(sessionId);
            return {
                feedback: status.alreadyProcessed ? 'Premium is already active on this account.' : 'Premium activated.',
                feedbackTone: 'success',
                status: status
            };
        } catch (error) {
            return {
                feedback: error && error.message ? error.message : 'Unable to confirm Stripe Checkout.',
                feedbackTone: 'error'
            };
        }
    }

    static getActiveProfileId () {
        try {
            if (window.ScratchJrAuth && typeof window.ScratchJrAuth.getActiveProfileId === 'function') {
                return window.ScratchJrAuth.getActiveProfileId() || DEFAULT_PROFILE_ID;
            }
        } catch (_) {}

        try {
            return (
                localStorage.getItem('scratchjr_profile_id') ||
                sessionStorage.getItem('scratchjr_profile_id') ||
                DEFAULT_PROFILE_ID
            );
        } catch (_) {
            return DEFAULT_PROFILE_ID;
        }
    }

    static async ensureProfileStoreReady () {
        if (!window.scratchJrDBService) {
            throw new Error('Profile storage is not available');
        }
        if (typeof window.scratchJrDBService.init === 'function') {
            await window.scratchJrDBService.init();
        }
    }

    static async getProfilesForAccount (accountId) {
        if (!accountId) {
            return [];
        }

        await Lobby.ensureProfileStoreReady();
        if (typeof window.scratchJrDBService.getProfilesByAccount !== 'function') {
            return [];
        }

        const profiles = await window.scratchJrDBService.getProfilesByAccount(accountId);
        return (profiles || []).filter((profile) => String(profile.id || '').trim() && profile.id !== DEFAULT_PROFILE_ID);
    }

    static async createChildProfile (accountId, displayName) {
        await Lobby.ensureProfileStoreReady();

        const profileId = `child-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
        await window.scratchJrDBService.saveProfile({
            id: profileId,
            accountId: accountId,
            displayName: displayName,
            avatar: Lobby.getProfileInitial(displayName),
            kind: 'child',
            isGuest: false
        });

        await Lobby.activateProfile(profileId);
        return profileId;
    }

    static bindProfileCardHandlers (card, container, handlers = {}) {
        const noop = function () {};
        const onSelect = handlers.onSelect || noop;
        const onDelete = handlers.onDelete;
        const deleteIcon = card.querySelector('.closex');
        let ignoreMouseUntil = 0;

        const runHandler = function (fcn) {
            Promise.resolve()
                .then(() => fcn())
                .catch((error) => {
                    console.error('[Lobby] Profile card action failed:', error);
                });
        };

        const handleClick = function (event) {
            const target = event && (event.target || (window.event && window.event.srcElement));
            const deleteVisible = deleteIcon && deleteIcon.style.visibility === 'visible';
            const clickedDeleteIcon = deleteVisible && target &&
                (target === deleteIcon || (deleteIcon.contains && deleteIcon.contains(target)));

            if (clickedDeleteIcon && onDelete) {
                runHandler(onDelete);
                return;
            }

            Lobby.hideProfileDeleteIcons(container);
            runHandler(onSelect);
        };

        const beginInteraction = function (event, isTouch) {
            if (!isTouch && (Date.now() < ignoreMouseUntil)) {
                if (event && typeof event.preventDefault === 'function') {
                    event.preventDefault();
                }
                if (event && typeof event.stopPropagation === 'function') {
                    event.stopPropagation();
                }
                return false;
            }

            if (isTouch) {
                ignoreMouseUntil = Date.now() + PROFILE_HOLD_MOUSE_SUPPRESSION_MS;
            }

            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            if (event && typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }

            Events.startDrag(
                event,
                card,
                noop,
                noop,
                noop,
                handleClick,
                (onDelete && deleteIcon) ? function () {
                    Lobby.showProfileDeleteIcon(container, card);
                } : undefined
            );

            return false;
        };

        card.ontouchstart = function (event) {
            return beginInteraction(event, true);
        };
        card.onmousedown = function (event) {
            return beginInteraction(event, false);
        };
        card.onclick = function (event) {
            if (event && typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            if (event && typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }
            return false;
        };
        card.onkeydown = function (event) {
            if (!event) {
                return;
            }

            if ((event.key === 'Enter') || (event.key === ' ')) {
                event.preventDefault();
                Lobby.hideProfileDeleteIcons(container);
                runHandler(onSelect);
                return;
            }

            if (onDelete && ((event.key === 'Delete') || (event.key === 'Backspace'))) {
                event.preventDefault();
                runHandler(onDelete);
            }
        };

        if (deleteIcon) {
            deleteIcon.onclick = function (event) {
                if (event && typeof event.preventDefault === 'function') {
                    event.preventDefault();
                }
                if (event && typeof event.stopPropagation === 'function') {
                    event.stopPropagation();
                }
                return false;
            };
        }
    }

    static showProfileDeleteIcon (container, card) {
        Lobby.hideProfileDeleteIcons(container, card);

        const deleteIcon = card && card.querySelector ? card.querySelector('.closex') : null;
        if (!deleteIcon) {
            return;
        }

        deleteIcon.style.visibility = 'visible';
        deleteIcon.style.pointerEvents = 'auto';
        card.classList.add('delete-ready');
    }

    static hideProfileDeleteIcons (container, exceptCard) {
        if (!container || !container.querySelectorAll) {
            return;
        }

        const cards = container.querySelectorAll('.settings-profile-card');
        for (let i = 0; i < cards.length; i++) {
            const card = cards[i];
            if (exceptCard && (card === exceptCard)) {
                continue;
            }

            const deleteIcon = card.querySelector('.closex');
            if (deleteIcon) {
                deleteIcon.style.visibility = 'hidden';
                deleteIcon.style.pointerEvents = 'none';
            }
            card.classList.remove('delete-ready');
        }
    }

    static async deleteChildProfile (accountId, profileId) {
        const resolvedProfileId = String(profileId || '').trim();
        if (!resolvedProfileId || (resolvedProfileId === DEFAULT_PROFILE_ID)) {
            throw new Error('Shared profile cannot be deleted');
        }

        await Lobby.ensureProfileStoreReady();

        let projects = [];
        if (typeof window.scratchJrDBService.listProjects === 'function') {
            projects = await window.scratchJrDBService.listProjects(resolvedProfileId);
        }

        for (let i = 0; i < projects.length; i++) {
            const project = projects[i];
            const projectId = String((project && project.id) || '').trim();
            if (!projectId) {
                continue;
            }

            try {
                Home.cleanupProjectFiles(projectId);
            } catch (_) {}

            await new Promise((resolve) => {
                try {
                    IO.deleteobject(OS.database, projectId, function () {
                        resolve();
                    });
                } catch (error) {
                    console.warn('[Lobby] Failed to delete project during profile removal:', projectId, error && error.message);
                    resolve();
                }
            });
        }

        await Lobby.clearProfileScopedData(resolvedProfileId);

        if (typeof window.scratchJrDBService.deleteProfile !== 'function') {
            throw new Error('Profile delete is not available');
        }

        await window.scratchJrDBService.deleteProfile(resolvedProfileId);
    }

    static async clearProfileScopedData (profileId) {
        const scopedKeys = [
            {dbKey: 'activity_progress', storageKey: 'scratchjr_activity_progress'},
            {dbKey: 'activity_projects', storageKey: 'scratchjr_activity_projects'},
            {dbKey: 'scratchjr_activity_badges', storageKey: 'scratchjr_activity_badges'}
        ];

        for (let i = 0; i < scopedKeys.length; i++) {
            const keyConfig = scopedKeys[i];
            let scoped = null;

            try {
                if (window.scratchJrDBService && (typeof window.scratchJrDBService.get === 'function')) {
                    const dbValue = await window.scratchJrDBService.get(keyConfig.dbKey);
                    if (dbValue) {
                        scoped = JSON.parse(dbValue);
                    }
                }
            } catch (_) {}

            if (!scoped) {
                try {
                    const storageValue = localStorage.getItem(keyConfig.storageKey);
                    if (storageValue) {
                        scoped = JSON.parse(storageValue);
                    }
                } catch (_) {}
            }

            if (!scoped || !scoped.profiles || (typeof scoped.profiles !== 'object')) {
                continue;
            }

            if (!Object.prototype.hasOwnProperty.call(scoped.profiles, profileId)) {
                continue;
            }

            delete scoped.profiles[profileId];

            const serialized = JSON.stringify(scoped);

            try {
                if (window.scratchJrDBService && (typeof window.scratchJrDBService.set === 'function')) {
                    await window.scratchJrDBService.set(keyConfig.dbKey, serialized);
                }
            } catch (_) {}

            try {
                localStorage.setItem(keyConfig.storageKey, serialized);
            } catch (_) {}
        }
    }

    static async activateProfile (profileId) {
        const resolvedProfileId = String(profileId || '').trim() || DEFAULT_PROFILE_ID;

        if (window.ScratchJrAuth && typeof window.ScratchJrAuth.setActiveProfile === 'function') {
            window.ScratchJrAuth.setActiveProfile(resolvedProfileId);
        } else {
            try {
                localStorage.setItem('scratchjr_profile_id', resolvedProfileId);
                sessionStorage.setItem('scratchjr_profile_id', resolvedProfileId);
            } catch (_) {}

            try {
                window.dispatchEvent(new CustomEvent('auth:profile-changed', {
                    detail: { profileId: resolvedProfileId }
                }));
            } catch (_) {}
        }

        try {
            if (window.Home && typeof window.Home.displayYourProjects === 'function') {
                window.Home.displayYourProjects();
            }
        } catch (_) {}

        return resolvedProfileId;
    }

    static async logoutToIndex () {
        try {
            if (window.ScratchJrAuth && typeof window.ScratchJrAuth.logoutWithApi === 'function') {
                await window.ScratchJrAuth.logoutWithApi({stayOnIndex: true, allDevices: true});
            } else {
                Lobby.clearLocalSession();
            }
        } catch (error) {
            console.warn('[Lobby] Logout failed, falling back to local clear:', error && error.message);
            Lobby.clearLocalSession();
        }

        window.location.href = 'index.html';
    }

    static clearLocalSession () {
        const keys = [
            'scratchjr_user_id',
            'scratchjr_access_token',
            'scratchjr_refresh_token',
            'scratchjr_email',
            'scratchjr_subscription_tier',
            'scratchjr_token_expiry',
            'scratchjr_profile_id'
        ];

        for (let i = 0; i < keys.length; i++) {
            try {
                localStorage.removeItem(keys[i]);
            } catch (_) {}
        }

        try { sessionStorage.removeItem('scratchjr_profile_id'); } catch (_) {}
        try { sessionStorage.removeItem('scratchjr_subscription_tier'); } catch (_) {}
        try { sessionStorage.removeItem('scratchjr_premium_expires_at'); } catch (_) {}
        try { sessionStorage.removeItem('__SSO_BYPASS_BETA_GATE__'); } catch (_) {}
        try { sessionStorage.removeItem('__SSO_PROVIDER__'); } catch (_) {}

        Lobby.updateActivityNavVisibility('free');

        try {
            window.dispatchEvent(new CustomEvent('auth:logout'));
        } catch (_) {}
    }

    static getProfileInitial (displayName) {
        const trimmed = String(displayName || '').trim();
        return trimmed ? trimmed.charAt(0).toUpperCase() : 'B';
    }

    static toSafeDomId (value) {
        return String(value || '').replace(/[^a-zA-Z0-9_-]+/g, '-');
    }

    static setSubMenu (page) {
        if (busy) {
            return;
        }
        document.ontouchmove = undefined;
        document.onmousemove = undefined;
        busy = true;
        ScratchAudio.sndFX('tap.wav');
        Lobby.selectSubButton(page);
        document.documentElement.scrollTop = 0;
        gn('wrapc').scrollTop = 0;
        var div = gn('wrapc');
        while (div.childElementCount > 0) {
            div.removeChild(div.childNodes[0]);
        }
        var url;
        switch (page) {
        case 'about':
            url = host + 'about.html';
            Lobby.loadLink(div, url, 'contentwrap scroll', 'htmlcontents scrolled');
            break;
        case 'interface':
            document.ontouchmove = function (e) {
                e.preventDefault();
            };
            document.onmousemove = function (e) {
                e.preventDefault();
            };
            url = host + 'interface.html';
            Lobby.loadLink(div, url, 'contentwrap noscroll', 'htmlcontents fixed');
            break;
        case 'paint':
            document.ontouchmove = function (e) {
                e.preventDefault();
            };
            document.onmousemove = function (e) {
                e.preventDefault();
            };
            url = host + 'paint.html';
            Lobby.loadLink(div, url, 'contentwrap noscroll', 'htmlcontents fixed');
            break;
        case 'blocks':
            url = host + 'blocks.html';
            Lobby.loadLink(div, url, 'contentwrap scroll', 'htmlcontents scrolled');
            break;
        case 'privacy':
            url = host + 'privacy.html';
            Lobby.loadLink(div, url, 'contentwrap scroll', 'htmlcontents scrolled');
            break;
        default:
            Lobby.missing(page, div);
            break;
        //url =  Lobby.loadProjects(div); break;
        }
    }

    static selectSubButton (str) {
        var list = ['about', 'interface', 'paint', 'blocks', 'privacy'];
        for (var i = 0; i < list.length; i++) {
            var kid = gn(list[i] + 'tab');
            var cls = kid.className.split(' ')[0];
            kid.className = cls + ((list[i] == str) ? ' on' : ' off');
        }
    }

    static selectButton (str) {
        var list = ['home', 'help', 'activity', 'book', 'gear'];
        for (var i = 0; i < list.length; i++) {
            if (str == list[i]) {
                gn(list[i] + 'tab').className = list[i] + ' on';
            } else {
                gn(list[i] + 'tab').className = list[i] + ' off';
            }
        }
    }

    static localizeOrFallback (key, fallback) {
        try {
            const localized = Localization.localize(key);
            if (localized && localized.indexOf('String missing:') !== 0) {
                return localized;
            }
        } catch (_) {}
        return fallback;
    }

    static loadLink (p, url, css, css2) {
        document.documentElement.scrollTop = 0;
        gn('wrapc').scrollTop = 0;
        gn('wrapc').className = css;
        var iframe = newHTML('iframe', 'htmlcontents', p);
        iframe.setAttribute('id', 'htmlcontents');
        iframe.style.visibility = 'hidden';
        gn('htmlcontents').className = css2;
        gn('htmlcontents').src = url;
        gn('htmlcontents').onload = function () {
            if (errorTimer) {
                clearTimeout(errorTimer);
            }
            errorTimer = undefined;
            busy = false;
            gn('wrapc').scrollTop = 0;
            iframe.style.visibility = 'visible';
        };
        errorTimer = window.setTimeout(function () {
            Lobby.errorLoading('Loading timeout');
        }, 20000);
    }

    static errorLoading (str) {
        if (errorTimer) {
            clearTimeout(errorTimer);
        }
        errorTimer = undefined;
        var wc = gn('wrapc');
        while (wc.childElementCount > 0) {
            wc.removeChild(wc.childNodes[0]);
        }
        var div = newHTML('div', 'htmlcontents', wc);
        div.setAttribute('id', 'htmlcontents');
        var ht = newHTML('div', 'errormsg', div);
        var h = newHTML('h1', undefined, ht);
        h.textContent = str;
        busy = false;
    }

    static missing (page, p) {
        gn('wrapc').className = 'contentwrap scroll';
        var div = newHTML('div', 'htmlcontents', p);
        div.setAttribute('id', 'htmlcontents');
        div = newHTML('div', 'errormsg', div);
        var h = newHTML('h1', undefined, div);
        h.textContent = page.toUpperCase() + ': UNDER CONSTRUCTION';
        busy = false;
    }

    static goHome () {
        if (currentPage === 'home') {
            window.location.href = 'index.html?back=true';
        } else {
            Lobby.setPage('home');
        }
    }

    static refresh () {
        if (gn('hometab') !== null) { // Check if we're on the lobby page
            // If DatabaseModule is present, refresh its cache before reloading
            const forceRender = () => {
                try {
                    console.log('🔁 Lobby.refresh: forcing Home render');
                    Lobby.changePage('home');
                } catch (e) {
                    console.warn('⚠️ Lobby.refresh() failed to change page:', e.message);
                }
            };

            try {
                if (window.dbModuleInitPromise && window.dbModule) {
                    Promise.resolve(window.dbModuleInitPromise)
                        .then(function(db){
                            var p = Promise.resolve(true);
                            if (db && typeof db.waitForWrites === 'function') { p = p.then(function(){ return db.waitForWrites(); }); }
                            if (db && typeof db.refreshCache === 'function') { p = p.then(function(){ return db.refreshCache(); }); }
                            return p;
                        })
                        .then(forceRender)
                        .catch(() => forceRender());
                } else {
                    forceRender();
                }
            } catch (_) {
                forceRender();
            }
        }
    }
}

window.Lobby = Lobby;
